<template>
  <a-layout-page class="join anim">
    <template #header>
      <a-logo brand />
    </template>
    <template #content>
      <div class="box">
        <div class="join__panel">
          <div class="join__panel__media">
            <template v-if="app.isCameraRecommended">
              <a-icon v-if="app.state.matches('app.camera.notAvailable')" name="camera-off" :width="80" :height="80" />
              <video v-else class="media media--cover" ref="video" autoplay playsinline muted></video>
            </template>
            <a-icon v-else name="person" :width="83" :height="100" />
          </div>
          <div class="join__panel__bar">
            <join-device-control-button v-if="isMicrophoneRecommended" device="microphone" id="microphone" />
            <join-device-control-button v-if="isCameraRecommended" device="camera" id="camera" />
            <join-devices-button />
            <join-qr-button />
          </div>
        </div>
        <div class="box__content">
          <a-alert v-if="response" :type="responseType">
            {{ response }}
          </a-alert>
          <form v-if="!areControlsHidden">
            <div class="form__group join__mode-toggle" v-if="!isConferenceInputDisabled">
              <a-button :variant="joinMode === 'pin' ? 'primary' : 'outline-primary'" @click="setJoinMode('pin')">
                {{ $t('join.usePinMode') }}
              </a-button>
              <a-button :variant="joinMode === 'key' ? 'primary' : 'outline-primary'" @click="setJoinMode('key')">
                {{ $t('join.useSidPidMode') }}
              </a-button>
            </div>
            <div class="form__group" v-if="joinMode === 'pin'">
              <label class="sr-only" for="pin">PIN</label>
              <input
                class="form__control text--center"
                type="text"
                name="pin"
                :value="pin"
                v-mask="'######'"
                :placeholder="$t('join.enterPin')"
                :disabled="isConferenceInputDisabled"
                inputmode="numeric"
                autocomplete="one-time-code"
                autocorrect="off"
                spellcheck="false"
                @input="handleInputChange($event)"
              />
            </div>
            <template v-else>
              <div class="form__group">
                <label class="sr-only" for="conference">Conference</label>
                <input
                  class="form__control text--center"
                  type="text"
                  :value="session.conference"
                  name="conference"
                  v-mask="'###-###-###-###'"
                  :placeholder="$t('join.enterSessionId')"
                  :disabled="isConferenceInputDisabled"
                  inputmode="numeric"
                  autocomplete="off"
                  autocorrect="off"
                  spellcheck="false"
                  @input="handleInputChange($event)"
                />
              </div>
              <div class="form__group">
                <label class="sr-only" for="key">Key</label>
                <input
                  class="form__control text--center"
                  type="text"
                  name="key"
                  :value="session.key"
                  :placeholder="$t('join.enterKey')"
                  v-mask="'XXXX'"
                  :hidden="isKeyInputHidden"
                  :disabled="isKeyInputDisabled"
                  autocomplete="off"
                  autocorrect="off"
                  spellcheck="false"
                  autocapitalize="none"
                  @input="handleInputChange($event)"
                />
              </div>
            </template>
            <div class="form-group">
              <a-button
                v-if="!isJoinButtonHidden && !isMeasurement"
                block
                :disabled="isJoinButtonDisabled || isJoining"
                @click="!isJoinButtonDisabled && joinHandler()"
              >
                {{ isJoining ? $t('join.action.joiningSession') : $t('join.action.startSession') }}
              </a-button>
              <a-button
                v-if="isMeasurement"
                block
              >
                <a-icon :name="`loading`" />
              </a-button>
            </div>
          </form>
        </div>
      </div>
    </template>
    <template #footer>
      <p class="text--small w--100 text--center" v-html="$t('join.agreement')"></p>
    </template>
    <template #image>
      <a-clock-image />
    </template>
  </a-layout-page>
</template>

<script setup>
import {onMounted, onUnmounted, computed, ref, watchEffect} from 'vue'
import {getURLParameter, matchURLParameter} from '@/utils'
import {useJoinMachine} from '@/state'
import {useLocalStream, useRepository, useWearML} from '@/composables'
import {locationMessages} from '@/messages'

import {useAppStore, useUserStore, useSessionStore} from '@/stores'

import {
  SessionNotStartedError,
  SessionNotFoundError,
  SessionExpiredError,
  SessionClosedError,
  PartyHasAlreadyJoinedError,
  UserNotAuthorizedError,
  UserNotFoundError,
  PinNotFoundError,
} from '@communication/errors'
import {useTranslation} from 'i18next-vue'
import ALayoutPage from '@/components/ALayoutPage.vue'
import axios from 'axios'
import AButton from '@/components/AButton.vue'
import {jsonRpc, mediator, messenger} from '@communication'
import Session from '@communication/Session'
import {useLanguage} from '@/composables'
import { usePermission } from '@vueuse/core'

const video = ref()
const session = useSessionStore()
const app = useAppStore()
const user = useUserStore()
const {state, send, service} = useJoinMachine()
const {video: localVideoStream, videoUpdatedAt} = useLocalStream()
const env = import.meta.env
const wearML = useWearML()

const {t, i18next} = useTranslation()
const {change: changeLanguage} = useLanguage()
const areControlsHidden = computed(() => state.value.context.areControlsHidden)
const isJoinButtonDisabled = computed(() => state.value.context.isJoinButtonDisabled)
const isJoinButtonHidden = computed(() => state.value.context.isJoinButtonHidden)
const isConferenceInputDisabled = computed(() => state.value.context.isConferenceInputDisabled)
const isKeyInputDisabled = computed(() => state.value.context.isKeyInputDisabled)
const isKeyInputHidden = computed(() => state.value.context.isKeyInputHidden)
const response = computed(() => state.value.context.response)
const responseType = computed(() => state.value.context.responseType)
const shouldAutoJoin = computed(() => state.value.context.shouldAutoJoin)
const environment = window.getEnvironmentFromURL()
const isCameraRecommended = app.isCameraRecommended
const isMicrophoneRecommended = app.isMicrophoneRecommended
const isJoining = ref(false)
const isMeasurement = ref(false)
const joinMode = ref('pin')
const pin = ref('')
const geolocationPerm = usePermission('geolocation')
// After a network change (also followed by a reload) the server keeps this party on its old, silent socket for
// 15-20 s and answers con:join with PARTY_HAS_ALREADY_JOINED meanwhile. Retried like JsonRpc's re-join, about 30 s.
// A signaling socket that is not open yet (slow network, or reconnecting) is waited for within the same attempts
const JOIN_RETRY_DELAY = 3000
const JOIN_RETRY_ATTEMPTS = 10
class JoinAbortedError extends Error {}
const connectionFailedMessage = () =>
  t('join.connectionFailed', {defaultValue: 'Could not connect to the session. Check your internet connection and try again.'})

const joinEventHandlersMap = new Map([
  ['validateSession', () => validateSession()],
  ['validateSessionAndJoin', () => validateSession()],
])

const serviceSubscription = service.subscribe(currentState => {
  if (joinEventHandlersMap.has(currentState.event.type)) {
    joinEventHandlersMap.get(currentState.event.type)(currentState)
  }
})

const canJoin = () => {
  if (joinMode.value === 'pin') {
    return /^\d{6}$/.test(pin.value)
  }
  const format = /^(\d{3}-){3}\d{3}$/
  return !!(session?.conference?.match(format) && session.key.length > 0)
}
const handleInputChange = (e) => {
  if(e.target.name === 'key'){
    session.key = e.target.value
  } else if(e.target.name === 'conference') {
    session.conference = e.target.value
  } else if (e.target.name === 'pin') {
    pin.value = e.target.value
  }
  canJoin() ? send('canJoin') : send('init')
}
const setJoinMode = (mode) => {
  joinMode.value = mode
  send('init')
}

const speedMeasurementPromise = ref(null)

watchEffect(() => {
  // Also depend on videoUpdatedAt: switching devices adds the new track to the same
  // MediaStream object, so the stream ref alone does not change
  videoUpdatedAt.value
  if (video?.value) {
    if (video.value.srcObject !== localVideoStream.value) {
      video.value.srcObject = localVideoStream.value
    }
    // In Firefox a preview attached while the stream was still empty stays paused
    // after the track arrives — autoplay alone does not resume it
    video.value.play().catch(() => {})
  }
})

messenger.subscribe('session', 'session:join', () => {
  joinSession()
})

messenger.subscribe('session:language', 'session:language:change', ({language}) => {
  changeLanguage(language)
})

onMounted(async () => {
  const pinParam = getURLParameter('pin') || ''
  const key = getURLParameter('k') || ''
  const format = /^(\d{3}-){3}\d{3}$/
  const conference = getURLParameter('conference') || matchURLParameter(format) || ''

  if (pinParam) {
    joinMode.value = 'pin'
    pin.value = pinParam
  } else if (conference || key) {
    joinMode.value = 'key'
    session.conference = conference
    session.key = key
  }

  send('hasStream')
  app.loaded()
  if (canJoin()) {
    send('canJoinUsingURLParams')
    send('validateSession')
  }
  wearML.refresh()

  await (speedMeasurementPromise.value = handlerSpeedMeasurement())
})

onUnmounted(() => {
  serviceSubscription.unsubscribe()
})

const validateSession = async event => {
  const {configuration} = app
  const {
    app: {type: appType},
    session: {api: sessionApiEndpoint, pinApi: pinApiEndpoint},
  } = configuration
  isJoining.value = true
  try {
    await speedMeasurementPromise.value
    if (joinMode.value === 'pin') {
      const {sessionId, partyId} = await Session.resolvePin(pinApiEndpoint, pin.value)
      session.conference = sessionId
      session.key = partyId
    }
    const {key, conference: callId} = session
    const sessionInfo = await mediator.createSession(sessionApiEndpoint, appType, key, callId)
    session.setInfo(sessionInfo)

    const isSessionAudioMuted = session?.info?.allowStartMuted
    if (isSessionAudioMuted) {
      app.transition('MUTE_MICROPHONE', {isSilent: true})
    }

    send('ready')
    // Before the auto-join, which sets it again for the join itself
    isJoining.value = false

    if (shouldAutoJoin.value) {
      joinSession()
    }
    return true
  } catch (error) {
    isJoining.value = false
    if (error instanceof SessionNotStartedError) {
      send({
        type: 'sessionNotStarted',
        message: t(`join.${error.code}`).replace(
          '{value}',
          new Date(error.response.sessionStart).toLocaleString(i18next.language)
        ),
      })
    } else if (error instanceof SessionNotFoundError) {
      send({type: 'sessionNotFound', message: t(`join.${error.code}`)})
    } else if (error instanceof SessionExpiredError) {
      send({type: 'sessionExpired', message: t(`join.${error.code}`)})
    } else if (error instanceof SessionClosedError) {
      send({type: 'sessionClosed', message: t(`join.${error.code}`)})
    } else if (error instanceof UserNotFoundError) {
      send({type: 'userNotFound', message: t(`join.${error.code}`)})
    } else if (error instanceof UserNotAuthorizedError) {
      send({type: 'userNotAuthorized', message: t(`join.${error.code}`)})
    } else if (error instanceof PinNotFoundError) {
      send({type: 'pinNotFound', message: t(`join.${error.code}`)})
    } else {
      send({type: 'joinFailed', message: connectionFailedMessage()})
    }
    console.error(error)
    return false
  }
}

const joinHandler = async event => {
  await speedMeasurementPromise.value
  const downloadSpeed = user.networkSpeed?.downloadSpeed
  const uploadSpeed = user.networkSpeed?.uploadSpeed
  const speedThreshold = Number(env.VITE_UPLOAD_DOWNLOAD_SPEED_AVERAGE) || 1024
  
  const hasSlowSpeed = 
    (downloadSpeed !== undefined && Number(downloadSpeed) < speedThreshold) ||
    (uploadSpeed !== undefined && Number(uploadSpeed) < speedThreshold)
  
  if (hasSlowSpeed) {
    app.transition('SHOW_SLOW_NETWORK_MODAL')
    return 
  }
  
  if (state.value.matches('ui.canJoin')) {
    send('validateSessionAndJoin')
  } else {
    await joinSession()
  }
}

const joinSession = async () => {
  // Ensure session is validated before joining
  if (!state.value.matches('ui.ready')) {
    isMeasurement.value = false
    const isValid = await validateSession()
    if (!isValid) {
      console.error('Session validation failed')
      return
    }
  }

  // Double check that mediator.session exists and has user info
  if (!mediator.session || !mediator.session.user) {
    console.error('Session not properly initialized')
    send({type: 'sessionNotFound', message: t('join.sessionNotFound')})
    return
  }

  try {
    isJoining.value = true
    const {user: userResponse, configuration: configurationResponse} = await joinWithRetry()
    user.setUser(userResponse.id, userResponse.email, userResponse.fullname, userResponse.party)
    session.setConfiguration(configurationResponse)
    app.transition('meeting')

    const {sendWsEvent} = useRepository()
    if(typeof user.location?.latitude === 'number' && typeof user.location?.longitude === 'number'){
      sendWsEvent(locationMessages.locationSet(user.location, session.info.account, session.info.team ?? '', session.info.license))
    }

    const expectedUrlParams = `?${session.conference}&k=${session.key}`
    if (window.location.search !== expectedUrlParams) {
      const newUrl = `${window.location.pathname}${expectedUrlParams}`
      window.history.pushState({}, '', newUrl)
    }
  } catch (error) {
    // error.readableMessage = this.locize.get(`join.action.generalError`)
    if (error instanceof PartyHasAlreadyJoinedError) {
      // Still held by a live connection after all attempts: most likely another tab, window or device of this party
      send({type: 'userAlreadyJoined', message: t('announcement.alreadyJoined', {defaultValue: t(`join.${error.code}`)})})
    } else if (error instanceof JoinAbortedError) {
      // The session ID, key or PIN was changed meanwhile: the new input is validated and joined on its own
    } else if (error?.message === 'SESSION_NOT_FOUND') {
      send({type: 'sessionNotFound', message: t('join.sessionNotFound')})
    } else if (error instanceof SessionExpiredError) {
      // Checked on the join screen while still valid, expired before Start
      send({type: 'sessionExpired', message: t(`join.${error.code}`)})
    } else {
      send({type: 'joinFailed', message: connectionFailedMessage()})
    }
    console.error(error)
  } finally {
    isJoining.value = false
  }
}

// PARTY_HAS_ALREADY_JOINED and a signaling socket that is not open (or closes during the request) are retried while
// the validated session is shown; any other error ends it at once. A failed attempt publishes nothing on the server
// (it fails before the join event), it only re-sends the call state to the parties already in the call
async function joinWithRetry() {
  const joiningSession = mediator.session
  for (let attempt = 1; ; attempt++) {
    let message = t('join.action.connecting', {defaultValue: 'Connecting...'})
    // con:join on a socket that is still opening fails at once, on a closed one it waits for the 60 s request timeout
    if (jsonRpc.status === 'CONNECTED') {
      try {
        return await joiningSession.join()
      } catch (error) {
        const retryable = error instanceof PartyHasAlreadyJoinedError || error?.message === 'WebSocket closed'
        if (!retryable || attempt >= JOIN_RETRY_ATTEMPTS) throw error
        if (error instanceof PartyHasAlreadyJoinedError) {
          message = t('join.action.reconnecting', {defaultValue: 'Reconnecting to session...'})
        }
      }
    } else if (attempt >= JOIN_RETRY_ATTEMPTS) {
      throw new Error('Signaling not connected')
    }
    send({type: 'joinRetrying', message})
    await new Promise(resolve => setTimeout(resolve, JOIN_RETRY_DELAY))
    // Left the join screen, or the input changed (a new session is validated): this join is not wanted any more
    if (!app.state.matches('app.view.join') || mediator.session !== joiningSession || !state.value.matches('ui.ready')) {
      throw new JoinAbortedError('Join abandoned')
    }
  }
}

const getApiUrl = (endpoint, environment) => {
  if (environment) {
    if (environment === 'prod') {
      return endpoint.replace('{{environment}}', '')
    } else {
      return endpoint.replace('{{environment}}', `${environment}.`)
    }
  }
  return endpoint
}

async function handlerSpeedMeasurement() {
  isMeasurement.value = true
  await handlerUploadSpeedMeasurement()
  await handleDownloadSpeedMeasurement()
  isMeasurement.value = false
}
async function handlerUploadSpeedMeasurement() {
  const startTime = performance.now()
  let uploadSpeedMeasurement = 0
  const stringSend = length => [...Array(length)].map(() => Math.random().toString(36)[2]).join('')
  try {
    const dataSend = stringSend(Number(env.VITE_NUMBER_OF_CHARACTER_UPLOAD))
    const response = await axios.post(`${getApiUrl(env.VITE_API_URL, environment)}/api/public/network/uploadSpeed`, dataSend)
    const endTime = performance.now()
    uploadSpeedMeasurement = ((dataSend.length * 8) / ((endTime - startTime) / 1000) / 1024).toFixed(2)
  } catch (error) {
    console.error('Error:', error)
  }
  user.setNetworkSpeed({
    uploadSpeed: uploadSpeedMeasurement,
  })
  return uploadSpeedMeasurement
}

async function handleDownloadSpeedMeasurement() {
  const startTime = performance.now()
  let downloadSpeedMeasurement = 0
  try {
    const response = await axios.post(`${getApiUrl(env.VITE_API_URL, environment)}/api/public/network/downloadSpeed`, {})
    const jsonString = JSON.stringify(response.data)
    const byteLength = new TextEncoder().encode(jsonString).length
    const endTime = performance.now()
    downloadSpeedMeasurement = ((byteLength * 8) / ((endTime - startTime) / 1000) / 1024).toFixed(2)
  } catch (error) {
    console.error('Error:', error)
  }
  user.setNetworkSpeed({
    downloadSpeed: downloadSpeedMeasurement,
  })
}
</script>

<style lang="scss">
$join-media-bg: $purple-200;
$join-media-color: $white;
$join-bar-spacer: map-get($spacers, 2);
$join-avatar-size: 2rem;

.join {
  z-index: $zindex-default;

  .modal {
    background-color: $backdrop;
    z-index: $zindex-modal;
  }
  &__mode-toggle {
    display: flex;
    gap: map-get($spacers, 2);
    .btn {
      flex: 1;
    }
  }
  &__panel {
    position: relative;
    margin-bottom: $box-spacer;
    &__media {
      position: relative;
      width: 100%;
      padding-top: (divide(9, 16) * 100%);
      height: 0;
      background-color: $join-media-bg;
      color: $join-media-color;
      .media,
      .icon {
        position: absolute;
      }
      .media {
        left: 0;
        top: 0;
      }
      .icon {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-height: 35%;
      }
    }
    &__bar {
      position: absolute;
      width: 100%;
      left: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      .btn {
        margin: $join-bar-spacer ($join-bar-spacer * 0.5);
      }
    }
  }
  &__contact {
    display: flex;
    align-items: center;
    &__avatar {
      flex: 0 0 $join-avatar-size;
      img {
        display: block;
        width: $join-avatar-size;
        height: $join-avatar-size;
        border-radius: 50%;
      }
    }
    &__text {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .layout__image {
    right: $box-spacer-lg;
    bottom: $box-spacer-lg;
    width: calc(50vw - #{$layout-base-max-width * 0.5} - #{$box-spacer-lg * 2});
  }
  @media (max-width: 1200px) {
    .layout__image {
      display: none;
    }
  }
  @media (max-height: $breakpoint-sm) {
    .layout--base__header {
      position: absolute;
      left: 0;
      top: 0;
      width: 100vw;
    }
    .layout--base__content {
      padding-top: 0;
    }
    h1 {
      display: none;
    }
  }
  @media (min-width: $breakpoint-sm) {
    .logo__brand {
      transition-property: opacity;
      transition-duration: $duration-slow;
      transition-delay: $duration-fast;
      opacity: 0;
    }
    &.anim .logo__brand {
      opacity: 1;
    }
  }
  @media (max-height: $breakpoint-sm) and (max-width: $breakpoint-lg) {
    $pictogram-size: 30px;
    .logo {
      position: absolute;
      width: $pictogram-size;
      left: 50%;
      transform: translateX(($layout-base-max-width * -0.5) - $pictogram-size) translateX($layout-spacer-sm * -1);
      overflow: hidden;
    }
  }
}
</style>
